import { LocalDate } from "@js-joda/core";
import { keepPreviousData, type UseQueryResult, useQueries } from "@tanstack/react-query";
import type { ResponseOf } from "../../../../../core/api";
import useApi from "../../../../../shared/hooks/useApi";
import {
  getEntityCalendarVisibleMonthDates,
  splitEntityCalendarRange,
  uniqueEntityCalendarItemsBy,
} from "../../../../entityProfileModal/sections/calendar/entityProfileCalendar.utils";
import type { PatientProfilePatient } from "../../../patientProfileModal.types";

/**
 * Visits, vacations, notes, tasks and flexible broadcasts of the patient calendar,
 * all from one request.
 */
export type PatientCalendarResponse = ResponseOf<"get", "./dashboard/patients/:patientId/calendar">;

export type PatientCalendarTasksResponse = PatientCalendarResponse["tasks"];

export type PatientCalendarFlexibleBroadcastsResponse = Pick<
  PatientCalendarResponse,
  "flexibleVisitBroadcasts"
>;

/**
 * Every calendar query of the patient starts with this key, so a refresh can
 * refetch all of them (calendar and list view, every range) at once.
 */
export function getPatientCalendarQueryKeyPrefix(patientId: PatientProfilePatient["id"]) {
  return ["patientProfileModal", patientId, "calendar"] as const;
}

export function usePatientProfileCalendarMonth(
  patientId: PatientProfilePatient["id"],
  monthDate: LocalDate,
  enabled = true
) {
  const visibleDates = getEntityCalendarVisibleMonthDates(monthDate);
  const calendarQuery = usePatientProfileCalendarRange(
    patientId,
    visibleDates.queryFrom,
    visibleDates.queryTo,
    enabled
  );

  return {
    calendarQuery,
    visibleDates,
  };
}

/**
 * The patient calendar for a range. Ranges longer than the endpoint accepts (only
 * the list view can pick one) are fetched in parts and merged.
 */
export function usePatientProfileCalendarRange(
  patientId: PatientProfilePatient["id"],
  from: LocalDate,
  to: LocalDate,
  enabled = true
) {
  const { api } = useApi();

  return useQueries({
    queries: splitEntityCalendarRange(from, to).map((range) => ({
      queryKey: [
        ...getPatientCalendarQueryKeyPrefix(patientId),
        range.from.toString(),
        range.to.toString(),
      ],
      queryFn: () =>
        api.get("./dashboard/patients/:patientId/calendar", {
          path: { patientId },
          query: {
            from: range.from,
            to: range.to,
          },
        }),
      enabled,
      placeholderData: keepPreviousData<PatientCalendarResponse>,
      // Refreshed explicitly after every change (`medflyt:refresh_visits`).
      // Refetching on focus reloaded every mounted calendar each time the
      // user came back to the tab.
      refetchOnWindowFocus: false,
    })),
    combine: combinePatientCalendarRanges,
  });
}

function combinePatientCalendarRanges(results: UseQueryResult<PatientCalendarResponse>[]) {
  const responses = results.flatMap((result) => (result.data === undefined ? [] : [result.data]));

  return {
    data:
      responses.length === results.length && responses.length > 0
        ? mergePatientCalendarResponses(responses)
        : undefined,
    isError: results.some((result) => result.isError),
    isFetched: results.every((result) => result.isFetched),
    isLoading: results.some((result) => result.isLoading),
    isPlaceholderData: results.some((result) => result.isPlaceholderData),
    isSuccess: results.every((result) => result.isSuccess),
    refetch: () => Promise.all(results.map((result) => result.refetch())),
  };
}

/**
 * A future task has no instance yet and can repeat under one task id, so its start
 * date is part of the key.
 */
function getPatientCalendarTaskKey(
  task: PatientCalendarTasksResponse["patientTaskInstances"][number]
) {
  const id = "taskInstanceId" in task ? task.taskInstanceId : task.taskId;

  return `${task.type}:${id}:${"startDate" in task ? task.startDate.toString() : ""}`;
}

function mergePatientCalendarResponses(
  responses: PatientCalendarResponse[]
): PatientCalendarResponse {
  if (responses.length === 1) {
    return responses[0];
  }

  return {
    visitInstances: uniqueEntityCalendarItemsBy(
      responses.flatMap((response) => response.visitInstances),
      (visitInstance) => visitInstance.visit.id
    ),
    patientVacations: uniqueEntityCalendarItemsBy(
      responses.flatMap((response) => response.patientVacations),
      (vacation) => vacation.id
    ),
    notes: uniqueEntityCalendarItemsBy(
      responses.flatMap((response) => response.notes),
      (note) => note.id
    ),
    tasks: {
      // Tasks overlap the range, so one spanning two ranges comes back twice.
      patientTaskInstances: uniqueEntityCalendarItemsBy(
        responses.flatMap((response) => response.tasks.patientTaskInstances),
        getPatientCalendarTaskKey
      ),
      caregivers: uniqueEntityCalendarItemsBy(
        responses.flatMap((response) => response.tasks.caregivers),
        (caregiver) => caregiver.id
      ),
      patients: uniqueEntityCalendarItemsBy(
        responses.flatMap((response) => response.tasks.patients),
        (patient) => patient.id
      ),
    },
    flexibleVisitBroadcasts: uniqueEntityCalendarItemsBy(
      responses.flatMap((response) => response.flexibleVisitBroadcasts),
      (broadcast) => broadcast.id
    ),
  };
}
